import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { BlogProvider, useBlog } from './context/BlogContext';
import Sidebar from './components/Sidebar';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import BlogPost from './pages/BlogPost';
import Philosophy from './pages/Philosophy';
import AboutUs from './pages/AboutUs';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import EditPost from './pages/EditPost';
import CreatePost from './pages/CreatePost';
import { IS_BETA_STAGE } from './config/appStage';

const DEFAULT_FAVICON = '/favicon.svg';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

function FaviconSync() {
  const { siteSettings } = useBlog();

  useEffect(() => {
    const faviconHref = siteSettings?.brandLogo || DEFAULT_FAVICON;

    const iconLinks = Array.from(document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']"));
    if (iconLinks.length === 0) {
      const link = document.createElement('link');
      link.setAttribute('rel', 'icon');
      link.setAttribute('href', faviconHref);
      document.head.appendChild(link);
      return;
    }

    iconLinks.forEach((link) => {
      link.setAttribute('href', faviconHref);
    });
  }, [siteSettings?.brandLogo]);

  return null;
}

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSidebarToggle = (val) => {
    if (isMobile) {
      setMobileMenuOpen(val);
    } else {
      setSidebarOpen(val);
    }
  };

  const effectiveOpen = isMobile ? true : sidebarOpen;

  return (
    <AuthProvider>
      <BlogProvider>
        <FaviconSync />
        <div className="app-layout">
          {/* Mobile hamburger */}
          {isMobile && !mobileMenuOpen && (
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}

          {/* Mobile overlay backdrop */}
          {isMobile && (
            <div
              className={`sidebar-overlay ${mobileMenuOpen ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            />
          )}

          <Sidebar
            open={effectiveOpen}
            setOpen={handleSidebarToggle}
            isMobile={isMobile}
            mobileOpen={mobileMenuOpen}
          />

          <main className="main-content" style={{
            marginLeft: isMobile ? 0 : (sidebarOpen ? 'var(--sidebar-width)' : '68px'),
            transition: 'margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            {IS_BETA_STAGE && (
              <div className="beta-stage-banner" role="status" aria-live="polite">
                Beta Stage: this environment is for testing and may be unstable.
              </div>
            )}
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/post/:id" element={<BlogPost />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/philosophy" element={<Philosophy />} />
              <Route path="/login" element={<Login />} />
              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/new" element={<ProtectedRoute role="admin"><CreatePost /></ProtectedRoute>} />
              <Route path="/admin/edit/:id" element={<ProtectedRoute role="admin"><EditPost /></ProtectedRoute>} />
            </Routes>
          </main>
        </div>
      </BlogProvider>
    </AuthProvider>
  );
}
